/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
/** @jsxImportSource @emotion/react */
import { gql, useMutation } from '@apollo/client';
import { css } from "@emotion/react";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import { MultiSelect } from 'primereact/multiselect';
import brace from "brace";
import AceEditor from "react-ace";
import "brace/mode/css";
import "brace/mode/javascript";
import "brace/theme/github";
import "brace/theme/twilight";
import "brace/theme/monokai";
import SectionHeader from "./helper/SectionHeader";
import { escapeForRegExpAndJSON, unescapeFromRegExpAndJSON } from "../utilities/utils";
import { useDispatch, useSelector } from "react-redux";
import { removeFromBuiltDomains } from "../reducers/buildReducer";
import deepcopy from "deepcopy";
import { pushMessage } from "../reducers/messagingReducer";
import LayoutWrapper from "./LayoutWrapper";
import { registerScreenChanges } from "../reducers/screenChangesReducer";
import useAdgridToolTipManager from "../customHooks/useAdgridToolTipManager";
import { useDomainSettings } from "../hooks/useDomainSettings";
import { useNativeInjectionSave } from "../hooks/useNativeInjectionSave";
import { MULTI_FIELD_READ_MUTATION } from "./adapters/utils/constants";


/**
 *
 * @param {string} str
 * @param {function(string): string} fn
 * @returns {string}
 */
function helper(str, fn) {
    return str
        .split('\n')
        .map(rule => {
            rule = rule.trim()
            if (rule.startsWith('reg=')) {
                rule = fn(rule)
            }
            return rule
        })
        .join('\n')
}

let insertTypes = [
    { code: "sibling", name: "Append as a Sibling" },
    { code: "psibling", name: "Prepend as a Sibling" },
    { code: "child", name: "Append as a Child" },
    { code: "pchild", name: "Prepend as a Child" },
    { code: "incontent", name: "In Content" },
];

const customDropdownItem = css`
    display: flex;
    flex-direction: row;
    align-items: center;
    .d-label {
        margin-right: 1em; 
        border: 1px solid #ccc;
        padding: 3px 6px;
        border-radius: 6px;
        span {
            font-weight: 600;
        }
    }
    .d-name {
        text-transform: capitalize; 
    }
`
const itemTemplate = item => {
    let isDisplay = !item?.synd
    let color = isDisplay ? '#20a4f3' : '#5EFC8D';

    return (
        <div css={css`${customDropdownItem}`}>
            <div className="d-label">
                <FontAwesomeIcon icon={faExclamationCircle} color={color} />
                <span>{isDisplay ? " Display " : " Syndication "}</span>
            </div>
            <div className="d-name">{item?.name || ""}</div>
        </div>
    )
}

const AD_INJECTION_FIELDS = {
    enable_native_ad_injection: {},
    native_ad_name: {
        label: "Name",
        type: "string",
    },
    native_ad_rep_ads_name: {
        label: "Ads Name",
        choices: [],
        type: "dropdown",
        fieldProps: {
            optionLabel: "name",
            optionValue: 'code',
        },
        customDropdownProps: {
            itemTemplate: itemTemplate,
            valueTemplate: itemTemplate,
        },
        mode: "display"
    },
    video_player_name: {
        label: "Video Player Name",
        choices: [],
        type: "dropdown",
        mode: "video",
        fieldProps: {
            optionLabel: "name",
            optionValue: 'code',
        },
    },
    target_wrapper: {
        label: "Target Wrapper",
        type: "string",
    },
    target_insert_type: {
        label: "Target Insert Type",
        choices: insertTypes,
        type: "dropdown",
        fieldProps: {
            optionLabel: "name",
            optionValue: 'code',
        },
    },
    words_no: {
        label: "Number Of Words",
        choices: [],
        value: "",
        type: "number",
        predicate: (_, settings) => {
            return settings.target_insert_type?.value?.code === 'incontent'
        }
    },
    exclude_target_wrapper: {
        label: "Exclude Target Wrapper",
        choices: [],
        value: "",
        type: "string",
        predicate: (_, settings) => {
            return settings.target_insert_type?.value?.code === 'incontent'
        }
    },
    target_position: {
        label: "Target Position (Repeat After)",
        type: "number"
    },
    stop_repeat_after: {
        label: "Stop Repeat After certain number of occurrences",
        type: "number"
    },
    target_urls: {
        label: "Inclusion URLs",
        type: "textarea",
    },
    exclutions_urls: {
        label: "Exclusion URLs",
        type: "textarea"
    },
    inclusion_kvps: {
        label: "Inclusion KVPs",
        type: "textarea"
    },
    exclusion_kvps: {
        label: "Exclusion KVPs",
        type: "textarea"
    },
    inclusions_user_agent: {
        label: "Inclusion User Agents",
        type: "textarea"
    },
    exclusions_user_agent: {
        label: "Exclusion User Agents",
        type: "textarea"
    },
    extra_ad_container_height: {
        label: "Extra Ad Container Height (Desktop & Tablet)",
        type: "string",
        dependsOn: "enableCls"
    },
    extra_ad_container_height_mob: {
        label: "Extra Ad Container Height (Mobile)",
        type: "string",
        dependsOn: "enableCls"
    },
    player_heading: {
        label: "Player Heading",
        type: "string",
        mode: "video"
    },
    close_button_delay: {
        label: "Close Button Delay (seconds)",
        type: "number",
        mode: "video"
    },
    custom_inline_style: {
        label: "Custom Inline Style",
        type: "code",
        language: "css",
    },
    custom_parameter_or_code: {
        label: "Custom Parameter or Custom Code",
        type: "code",
        language: "javascript",
        mode: "display"
    },
    is_hidden_ad_target_wrapper: {
        label: "Is Hidden Ad Target Wrapper",
        type: "switch",
        isAdminOnly: true,
        mode: "display"
    },
    enable_shadowdom_support: {
        label: "Enable ShadowDOM Support",
        type: "switch",
        mode: "display"
    },
    shadowdom_target_wrapper: {
        label: "Shadow Host Target Wrapper",
        type: "string",
        mode: "display",
        enabledOn: "enable_shadowdom_support"
    },
    float_player_on_load: {
        label: "Float Player On Load",
        type: "switch",
        mode: "video",
    },
    video_title: {
        label: "Video Title ",
        type: "switch",
        mode: "video"
    },
    enable_on_preview: {
        label: "Enable On Preview",
        type: "switch",
        mode: ""
    },
    disable_repeat: {
        label: "Disable Repeat",
        type: "switch",
        mode: "video"
    },
    disable_refresh: {
        label: "Disable Refresh",
        type: "switch",
        mode: "display"
    },
    enable_native_ad_for_ajax: {
        label: "Enable Native Ad for AJAX",
        type: "switch",
        mode: "display"
    },
    reset_repeat_counter: {
        label: "Reset Repeat Counter",
        type: "switch",
    },
    advertisement_label: {
        label: "Advertisement Label",
        type: "switch",
        mode: "display"
    },
    advertisement_label_position: {
        label: "Advertisement Label Position",
        mode: "display",
        choices: [
            { code: "before", name: "Before" },
            { code: "after", name: "After" },
        ],
        fieldProps: {
            optionLabel: "name",
            optionValue: 'code',
        },
        type: "dropdown",
        enabledOn: "advertisement_label"
    },
    advertisement_label_align: {
        label: "Advertisement Label Align", // this one has a default value in the old code
        mode: "display",
        choices: [
            { value: "center", name: "Center" },
            { value: "left", name: "Left" },
            { value: "right", name: "Right" },
        ],
        type: "dropdown",
        fieldProps: {
            optionLabel: "name",
            optionValue: 'value',
        },
        enabledOn: "advertisement_label"
    },
    enable_companion_ads: {
        label: "Enable Companion Ads",
        type: "switch",
        mode: "video",
        predicate: playerVendor => {
            return playerVendor === 'brid_player'
        }
    },
    companion_ads_positions: {
        label: "Target Companion Ad Positions",
        type: "multiselect",
        mode: "video",
        choices: [],
        enabledOn: "enable_companion_ads"
    }
}

const AdNativeInjection = ({ mode }) => {
    const CODE_TYPE = "native";
    const [mutationDataCompleted, setMutationDataCompleted] = useState(false);
    const [globalState, setGlobalState] = useState({});
    /** @type {[NativeAdConfiguration[], React.Dispatch<React.SetStateAction<NativeAdConfiguration[]>>]} */
    const [settingsData, setSettingsData] = useState([]);
    const [displayConfirmation, setDisplayConfirmation] = useState(false);
    const [positionFinal, setPositionFinal] = useState(null);
    const { currentDomain, currentCustomer, isAdmin, customers } = useSelector((state) => state.userInformation);
    const commonDomainInfo = useSelector((state) => state.commonDomainInfo);
    const dispatch = useDispatch();
    const { toolTipTextWithAttributes: tooltip, toolTipText: helpText } = useAdgridToolTipManager('injections');
    const [activeIndex, setActiveIndex] = useState([])
    const [multiFieldRetrieveMutation] = useMutation(MULTI_FIELD_READ_MUTATION)
    const [syndicationPositions, setSyndicationPositions] = useState([])
    const [showSyndicationPositions, setShowSyndicationPositions] = useState(false)

    const handleRemoveClick = (index) => {
        const list = [...settingsData];
        list.splice(index, 1);
        setSettingsData(list);
        dispatch(registerScreenChanges("Ad Injection"));
    };

    const handleAddClick = () => {
        let copy = deepcopy(AD_INJECTION_FIELDS)
        copy.advertisement_label_position = {
            value: { code: commonDomainInfo[currentDomain]?.advertisement_label_position || copy.advertisement_label_position.choices[1].code },
        };
        copy.advertisement_label_align = {
            value: commonDomainInfo[currentDomain]?.advertisement_label_align || copy.advertisement_label_align.choices[0].value,
        };
        setSettingsData([...settingsData, copy]);
        dispatch(registerScreenChanges("Ad Injection"));
    };

    const handleChange = (i, name, value) => {
        let temp = [...settingsData];
        let err = false;

        if (name === "target_position" && (value < -1 || value > 100)) {
            dispatch(pushMessage("Ad Exclusion", "Target position value must be between -1 and 100. Resetting", "error"));
            err = true;
        }

        temp[i][name] = temp[i]?.[name] || {};

        if (name === 'enable_native_ad_injection') {
            temp[i][name].value = value
        } else {
            temp[i][name].value = err ? 0 : value;
        }

        setSettingsData(temp);
        dispatch(registerScreenChanges("Ad Injection"));
    };

    const DOMAIN_SETTINGS_MUTATION = useDomainSettings({
        onCompleted(data) {

            let temp_data = JSON.parse(data.domainEditMutation.customerInfo);

            let isVideo = mode === 'video'
            /** @type {NativeAdConfiguration[] | null} */
            let adInjections = temp_data[isVideo ? "video_player_injections" : "native_ad_injections"]; // temp_data.native_ad_injections
            let adPositions = temp_data[isVideo ? "video_player_positions" : "ad_positions"] || {};
            let initiallyEmtpy = false;

            if (isVideo) {
                let displayAdPositions = Object
                    .values(temp_data['ad_positions'] || {})
                    .reduce((acc, curr) => {
                        let { display_name, slot_name: acfFieldName } = curr
                        acc.push({
                            name: display_name,
                            value: acfFieldName
                        })
                        return acc
                    }, [])

                AD_INJECTION_FIELDS.companion_ads_positions.choices = displayAdPositions || []
            }

            AD_INJECTION_FIELDS.native_ad_rep_ads_name.choices = []

            if (mode === "display") {
                for (let { display_name: name, slot_name: code } of Object.values(adPositions)) {
                    AD_INJECTION_FIELDS.native_ad_rep_ads_name.choices.push({ name, code })
                }
                AD_INJECTION_FIELDS.native_ad_rep_ads_name.choices.push(...syndicationPositions)
            } else {
                for (let o of Object.values(adPositions)) {
                    let { displayName: name, playerSlot: code } = o;
                    AD_INJECTION_FIELDS.video_player_name.choices.push({ name, code })
                }
            }

            setGlobalState({ enableCls: !!temp_data.enable_cls, playerVendor: temp_data.player_vendor })

            if (Array.isArray(adInjections) && adInjections.length) {
                let prop = isVideo ? "video_player_injections" : "native_ad_injections"
                temp_data[prop] = adInjections.filter((item) => {
                    let collection = isVideo
                        ? item.video_player_name.choices
                        : item.native_ad_rep_ads_name.choices

                    if (item.exclutions_urls.value && item.exclutions_urls.value.includes('reg=')) {
                        item.exclutions_urls.value = helper(item.exclutions_urls.value, unescapeFromRegExpAndJSON)
                    }

                    if (item.target_urls.value && item.target_urls.value.includes('reg=')) {
                        item.target_urls.value = helper(item.target_urls.value, unescapeFromRegExpAndJSON)
                    }

                    if (Array.isArray(collection)) {
                        collection = collection.filter(i => i.code !== 'customCode')
                    }

                    try {
                        if (item.companion_ads_positions?.value) {
                            item.companion_ads_positions.value = JSON.parse(item.companion_ads_positions.value)
                        }
                    } catch (error) {
                        console.log({ error })
                    }

                    return isVideo ? item.video_player_name.value.code !== "customCode" : item.native_ad_rep_ads_name.value.code !== "customCode";
                });
            } else {
                initiallyEmtpy = true;
            }

            if (!initiallyEmtpy && mode !== "video") {
                adInjections.map((injection) => {
                    injection.useDefaultLabelPosition = !injection?.advertisement_label?.value;
                    return injection;
                });
            }

            setSettingsData(initiallyEmtpy ? [] : adInjections);
            setMutationDataCompleted(true);
        },
    })

    const SAVE_NATIVE_AD_INJECTION_MUTATION_CONTAINER = useNativeInjectionSave({
        onCompleted() {
            dispatch(pushMessage("Ad Injection", "Native ad injection data updated"));
            removeFromBuiltDomains(`${currentDomain}`);
        }
    })

    useEffect(() => {
        if (currentCustomer && currentDomain) {
            /* alloswedCustomers are 'adgrid' and 'content zebra' */
            let allowedCustomers = new Set(['372', '31911'])
            setShowSyndicationPositions(allowedCustomers.has(currentCustomer))
            multiFieldRetrieveMutation({
                variables: {
                    fieldDefinitions: JSON.stringify([
                        { field_name: 'syndication_ad_positions', post_id: currentDomain, parse: true }
                    ])
                }
            }).then(response => {
                try {
                    let syndicationPositions = JSON.parse(response.data.multiFieldRetrieveMutation.response || '{}')
                    let options = []
                    for (let [positionName] of Object.entries(syndicationPositions['syndication_ad_positions'] || {})) {
                        options.push(
                            { name: positionName.replaceAll('_', ' '), code: positionName, synd: true }
                        )
                    }
                    setSyndicationPositions(options)
                } catch {
                    // do nothing
                }
            }).then(() => {
                DOMAIN_SETTINGS_MUTATION({
                    domainId: currentDomain,
                    customerId: currentCustomer
                })
            })
        }
    }, [currentCustomer, currentDomain, mode, customers]);

    const validityCheckCssSelector = (label, cssSelector) => {
        try {
            let fragmentElement = new DocumentFragment();
            fragmentElement.querySelector(cssSelector);
            return true;
        } catch (e) {
            dispatch(pushMessage(label, "Incorrect CSS selector! Please provide the correct CSS selector for the system to function properly.", "error"));
            return false;
        }
    }

    const formValidationAction = () => {
        let fieldObjectArr = [
            { machineName: 'native_ad_name', humanName: 'Name', msg: "this field is required", type: 'string' },
            { machineName: 'target_wrapper', humanName: 'Target Wrapper', msg: "this field is required", type: 'string', customRule: validityCheckCssSelector },
            { machineName: 'target_insert_type', humanName: 'Target Insert Type', msg: "this field is required", type: 'object', objectProperty: 'code' },
            { machineName: 'target_position', humanName: 'Target Position (Repeat After)', msg: "this field is required", type: 'string' },
            { machineName: 'target_urls', humanName: 'Exclusions URLs', msg: "this field is required", type: 'string' },
        ];

        let validationError = false;

        for (const settingsDataElement of settingsData) {

            for (const fieldObjectArrElement of fieldObjectArr) {

                let type = fieldObjectArrElement.type;
                let customRule = fieldObjectArrElement.customRule;

                if (type === 'string') {
                    let isFieldBlank = settingsDataElement[fieldObjectArrElement.machineName].value;
                    isFieldBlank = isFieldBlank === 0 || isFieldBlank;

                    if (!isFieldBlank) {
                        dispatch(pushMessage(fieldObjectArrElement.humanName, fieldObjectArrElement.msg, "error"));
                        validationError = true;
                    }
                }

                if (type === 'object') {
                    let isFieldBlank = settingsDataElement[fieldObjectArrElement.machineName].value[fieldObjectArrElement.objectProperty];
                    if (!isFieldBlank) {
                        dispatch(pushMessage(fieldObjectArrElement.humanName, fieldObjectArrElement.msg, "error"));
                        validationError = true;
                    }
                }

                if (customRule && !customRule(fieldObjectArrElement.humanName, settingsDataElement[fieldObjectArrElement.machineName].value)) {
                    validationError = true;
                }
            }
        }

        if (validationError) {
            throw Error('Validation error !');
        }
    }


    const saveAdNativeInjectionSettings = async () => {
        formValidationAction();
        dispatch(pushMessage("Ad Injection", "Saving ad injection edits..."));

        /* deep-copying state to avoid mutating the object below */
        let copy = deepcopy(settingsData)
        let response = await SAVE_NATIVE_AD_INJECTION_MUTATION_CONTAINER({
            mode,
            domainId: currentDomain,
            injectionType: CODE_TYPE,
            injectionValueList: JSON.stringify(
                copy.map((injection) => {
                    delete injection["useDefaultLabelPosition"];
                    let inclusionRules = injection.target_urls
                    let exclusionRules = injection.exclude_target_wrapper

                    if (inclusionRules && inclusionRules.value) {
                        injection.target_urls.value = helper(
                            inclusionRules.value,
                            escapeForRegExpAndJSON
                        )
                    }

                    if (exclusionRules && exclusionRules.value) {
                        injection.exclude_target_wrapper.value = helper(
                            exclusionRules.value,
                            escapeForRegExpAndJSON
                        )
                    }

                    return injection;
                })
            )
        });

        return response;
    };

    const dialogFuncMap = {
        displayConfirmation: setDisplayConfirmation,
    };

    const onClick = (name, index) => {
        setPositionFinal(index);
        dialogFuncMap[`${name}`](true);
    };

    const onHide = (name) => {
        dialogFuncMap[`${name}`](false);
    };

    const renderFooter = (name) => {
        return (
            <div>
                <Button label="No" icon="pi pi-times" onClick={() => onHide(name)} className="p-button-text" />
                <Button
                    label="Yes"
                    icon="pi pi-check"
                    onClick={() => {
                        onHide(name);
                        handleRemoveClick(positionFinal);
                    }}
                    autoFocus
                />
            </div>
        );
    };

    return (
        <LayoutWrapper
            model="domain"
            saveCallback={async () => {
                try {
                    let response = await saveAdNativeInjectionSettings()
                    return response
                } catch (err) {
                    dispatch(pushMessage('Error', err.message, 'error'))
                    return null
                }
            }}>

            {
                mutationDataCompleted && (
                    <div className="p-grid">
                        <div className="small-full-width p-col p-col-content">
                            <div className="p-panel p-component">
                                <SectionHeader screenName="Ad injection">
                                    <div className="p-text-bold">{mode === "video" ? "Video Ad Injection" : "Ad Injection"}</div>
                                </SectionHeader>
                                <div className="p-panel-content">
                                    <Dialog
                                        header="Confirmation"
                                        visible={displayConfirmation}
                                        modal
                                        style={{ width: "350px" }}
                                        footer={renderFooter("displayConfirmation")}
                                        onHide={() => onHide("displayConfirmation")}
                                    >
                                        <div className="confirmation-content p-d-flex p-ai-center">
                                            <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: "2rem" }} />
                                            <span>Are you sure you want to delete this item?</span>
                                        </div>
                                    </Dialog>

                                    <Accordion
                                        multiple
                                        activeIndex={activeIndex}
                                        onTabChange={e => {
                                            /** @type {HTMLElement} */
                                            let t = e.originalEvent.target
                                            // If we're clickin on the little switch to enable/disable an injection
                                            // return early to prevent the tab from expanding or collapsing
                                            if (t.classList.contains('p-inputswitch-slider')) {
                                                return
                                            }
                                            setActiveIndex(e.index)
                                        }}
                                    >
                                        {
                                            settingsData.map((rule, idx) => {
                                                let defaultLabelContainerClassname = "p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0"
                                                let defaultValueContainerClassname = "p-col-12 p-sm-12 p-md-9 p-lg-9"
                                                let acfFields = Object.keys(AD_INJECTION_FIELDS)

                                                return (
                                                    <AccordionTab
                                                        key={idx}
                                                        header={<div className="p-d-flex p-jc-between">
                                                            <div className="p-field-checkbox p-mb-0 native-input-switch">
                                                                <InputSwitch
                                                                    id={"enable_native_ad_injection" + idx}
                                                                    name={"enable_native_ad_injection"}
                                                                    checked={rule.enable_native_ad_injection.value}
                                                                    onChange={(e) => {
                                                                        handleChange(idx, e.target.name, e.target.value)
                                                                    }}
                                                                />
                                                                <div className="p-ml-2">{rule.native_ad_name.value}</div>
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                icon="pi pi-times"
                                                                className="p-button-small p-button-danger p-button-rounded p-button-text p-button-icon-only"
                                                                tooltip="Remove row"
                                                                tooltipOptions={{ position: "top" }}
                                                                onClick={() => onClick("displayConfirmation", idx)}
                                                            />
                                                        </div>}
                                                    >
                                                        <div className="p-grid p-fluid">
                                                            <Tooltip target=".adpositiontooltip" />
                                                            {
                                                                acfFields.map((acfFieldName, index) => {
                                                                    let {
                                                                        label,
                                                                        type,
                                                                        choices,
                                                                        mode: constraint,
                                                                        language,
                                                                        isAdminOnly,
                                                                        enabledOn,
                                                                        dependsOn,
                                                                        predicate,
                                                                        fieldProps,
                                                                        customDropdownProps
                                                                    } = AD_INJECTION_FIELDS[acfFieldName]
                                                                    customDropdownProps = customDropdownProps || {}
                                                                    let Component = InputText
                                                                    choices = choices || []

                                                                    if (!showSyndicationPositions) {
                                                                        customDropdownProps = {}
                                                                    }

                                                                    // This variable controls wheter any of the UI components below (i.e. input/textarea) will get rendered or not
                                                                    let willReturnEarly = (constraint && constraint !== mode)
                                                                        || (isAdminOnly && !isAdmin)
                                                                        || (dependsOn && !globalState[dependsOn])
                                                                        || (predicate && !predicate(globalState.playerVendor, rule || {}))


                                                                    if (willReturnEarly) {
                                                                        return null
                                                                    }

                                                                    if (type) {
                                                                        let { value } = rule[acfFieldName] || {}
                                                                        let props = {
                                                                            name: acfFieldName,
                                                                            value,
                                                                            onChange: e => {
                                                                                handleChange(idx, acfFieldName, e.target?.value || 0)
                                                                            }
                                                                        }

                                                                        if (enabledOn && !rule[enabledOn]?.value) {
                                                                            return null
                                                                        }

                                                                        if (type === 'string' || type === 'textarea') {
                                                                            Component = type === 'string'
                                                                                ? InputText
                                                                                : InputTextarea
                                                                            props = {
                                                                                ...props,
                                                                                value: value || ''
                                                                            }
                                                                        } else if (type === 'number') {
                                                                            Component = InputNumber
                                                                            props = {
                                                                                ...props,
                                                                                value: value || 0,
                                                                                onChange: e => {
                                                                                    handleChange(idx, acfFieldName, e.value || 0)
                                                                                }
                                                                            }
                                                                        } else if (type === 'dropdown') {
                                                                            Component = Dropdown
                                                                            fieldProps = fieldProps || {}
                                                                            let optionValue = fieldProps?.optionValue || 'value'
                                                                            try {
                                                                                if (fieldProps.optionValue) {
                                                                                    if (acfFieldName === 'advertisement_label_align') {
                                                                                        fieldProps.value = value
                                                                                    } else {
                                                                                        fieldProps.value = value?.[optionValue]
                                                                                    }
                                                                                }
                                                                            } catch (err) {
                                                                                console.log({ acfFieldName, value })
                                                                            }
                                                                            props = {
                                                                                ...props,
                                                                                options: choices,
                                                                                optionLabel: "name",
                                                                                optionValue: "value",
                                                                                onChange: e => {
                                                                                    handleChange(
                                                                                        idx,
                                                                                        acfFieldName,
                                                                                        acfFieldName === 'advertisement_label_align'
                                                                                            ? e.value
                                                                                            : { [optionValue]: e.value || 0 }
                                                                                    )
                                                                                },
                                                                                ...fieldProps,
                                                                                ...customDropdownProps
                                                                            }
                                                                        } else if (type === 'multiselect') {
                                                                            Component = MultiSelect
                                                                            props = {
                                                                                ...props,
                                                                                options: choices,
                                                                                display: "chip",
                                                                                optionLabel: "name",
                                                                                optionValue: "value",
                                                                                filter: true
                                                                            }
                                                                        } else if (type === 'code') {
                                                                            Component = AceEditor
                                                                            props = {
                                                                                mode: language,
                                                                                name: acfFieldName,
                                                                                theme: 'monokai',
                                                                                value: value || '',
                                                                                editorProps: {
                                                                                    $blockScrolling: true
                                                                                },
                                                                                onChange: e => {
                                                                                    handleChange(idx, acfFieldName, e)
                                                                                }
                                                                            }
                                                                        } else if (type === "switch") {
                                                                            Component = InputSwitch
                                                                            delete props.value
                                                                            props.checked = !!value

                                                                            return (
                                                                                <React.Fragment key={acfFieldName + "_" + index}>
                                                                                    <div className="p-col-12 p-sm-12 p-md-15 p-lg-12 p-field-checkbox">
                                                                                        <Component {...props} />
                                                                                        <label {...tooltip(acfFieldName, true)}>{label}</label>
                                                                                    </div>
                                                                                </React.Fragment>
                                                                            )
                                                                        }

                                                                        return (
                                                                            <React.Fragment key={acfFieldName + "_" + index}>
                                                                                <div className={defaultLabelContainerClassname}>
                                                                                    <label
                                                                                        className="adpositiontooltip after"
                                                                                        {...tooltip(acfFieldName, true)}
                                                                                    >
                                                                                        {label}
                                                                                    </label>
                                                                                </div>
                                                                                <div className={defaultValueContainerClassname}>
                                                                                    <Component {...props} />
                                                                                </div>
                                                                            </React.Fragment>
                                                                        )
                                                                    }

                                                                    return null
                                                                })
                                                            }
                                                        </div>
                                                    </AccordionTab>
                                                )
                                            })
                                        }
                                    </Accordion>
                                    <div className="p-mt-3 p-d-flex p-jc-end">
                                        <Button type="button" label="Add Row" tooltip="Add row" icon="pi pi-plus" tooltipOptions={{ position: "top" }} onClick={handleAddClick} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="small-full-width p-col p-col-sidebar">
                            <div className="p-panel p-component">
                                <SectionHeader>
                                    <div className="p-text-bold">Descriptions</div>
                                </SectionHeader>
                                <div className="p-panel-content p-fluid">
                                    <h6 className="p-text-bold p-mb-0">Target Wrapper</h6>
                                    <p>{helpText('help_text____target_wrapper')}</p>
                                    <h6 className="p-text-bold p-mb-0">Target Position (Repeat After)</h6>
                                    <p>{helpText('help_text____target_position_repeat_after')}</p>
                                    <h6 className="p-text-bold p-mb-0">Number of Words</h6>
                                    <p>{helpText('help_text____number_of_words')}</p>
                                    <h6 className="p-text-bold p-mb-0 m-mt-0">Inclusions URLs</h6>
                                    <p>{helpText('help_text____inclusions_urls')}</p>
                                    <h6 className="p-text-bold p-mb-0 m-mt-0">Exclusion KVPs</h6>
                                    <p>{helpText('help_text____exclusion_kvps')}</p>
                                    <h6 className="p-text-bold p-mb-0">Inclusions User Agent</h6>
                                    <p>{helpText('help_text____inclusions_user_agent')}</p>
                                    <h6 className="p-text-bold p-mb-0">Custom Inline Style</h6>
                                    <p>{helpText('help_text____custom_inline_style')}</p>
                                    <h6 className="p-text-bold p-mb-0">Custom Parameter or Custom Code</h6>
                                    <p>{helpText('help_text____custom_parameter_or_custom_code')}</p>
                                    <h6 className="p-text-bold p-mb-0">Disable repeat after number of occurrences</h6>
                                    <p>{helpText('help_text____disable_repeat_after_number_of_occurrences')}</p>
                                    <h6 className="p-text-bold p-mb-0">Reset repeat counter on AJAX loaded new pages</h6>
                                    <p>{helpText('help_text____reset_repeat_counter_on_ajax_loaded_new_pages')}</p>
                                    <h6 className="p-text-bold p-mb-0">Advertisement Label</h6>
                                    <p>{helpText('help_text____advertisement_label')}</p>
                                    <h6 className="p-text-bold p-mb-0">Advertisement Label Position</h6>
                                    <p>{helpText('help_text____advertisement_label_position')}</p>
                                    <h6 className="p-text-bold p-mb-0">Extra Ad Container Height (Desktop & Tablet / Mobile)</h6>
                                    <p>{helpText('help_text____extra_ad_container_height_desktop_&_tablet__mobile')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </LayoutWrapper>
    );
};

export default AdNativeInjection;
